/**
 * Copa 2026 — Serviço de acesso ao backend Funifier.
 *
 * Contratos verificados (backend já construído):
 *   - openPack  → POST database/copa_pacote__c   → before_create trigger retorna entity.stickers[]
 *                 (rejeições voltam como HTTP 200 com body.errors[])
 *   - collate   → POST database/copa_album__c     → after_create trigger valida e aplica
 *                 (rejeições voltam como HTTP 200 com body.errors[])
 *   - getAlbum  → POST find/copa_album_state      → prepared aggregate:
 *                 { album: CollatedSticker[] (flat), repository[], completedCount }
 *   - getChallenges → reusa find/challenge         → filter extra.type === 'copa' client-side
 *   - getCopaConfig → GET database/global__c       → doc 'global' com copa.{enabled,doublePack,states,positions}
 *
 * Nota (A7 PENDENTE): o token de player precisa de permissão de execução do prepared
 * aggregate e de create nas coleções copa_*. As permissões ainda não foram provisionadas —
 * o serviço está correto, mas chamadas retornarão 403 até lá.
 */

import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { api } from './api';
import { getChallengeUser } from './challenges.service';
import { getGlobalConfig } from './global.service';

const TIMEZONE = 'America/Sao_Paulo';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type StickerType = 'titular' | 'craque' | 'tecnico' | 'lendaria';

export interface Sticker {
  recognitionId: string;
  type: StickerType;
  behavior: string;
  colleagueId: string;
  colleagueName?: string;
  /** Foto do colega (player.image) — 1ª opção de face (ver StickerCard) */
  image?: { small?: { url?: string } } | null;
  /** Avatar nice-avatar do colega (player.extra.avatar) — 2ª opção */
  avatar?: Record<string, any> | null;
  /** Tipo de avatar do colega (player.extra.avatar_type): 0 = avatar, 1 = foto */
  avatarType?: number;
  /** Figurinha épica (craque) recebe o efeito holográfico */
  isEpic?: boolean;
}

/** Um sticker do repositório (sorteado mas ainda não colado) */
export interface RepositorySticker extends Sticker {
  /** true = já colado em alguma posição */
  collected: boolean;
  /** true = colega já foi colado por este jogador em outra posição */
  colleagueLocked: boolean;
  /** Pode ser colado agora */
  collatable: boolean;
}

/**
 * Documento de figurinha colada, como retornado (flat) pelo prepared aggregate.
 * O campo `position` aponta para a chave da posição do álbum (ex: 'goleiro').
 */
export interface CollatedSticker {
  /** _id do doc copa_album__c — necessário para a troca (PUT in-place, RN-31/ADR-20) */
  _id?: string;
  position: string;
  tipo: StickerType;
  behavior: string;
  colleagueId: string;
  recognitionId: string;
  colleagueName?: string;
  /** Face do colega (foto → avatar → inicial) */
  image?: { small?: { url?: string } } | null;
  avatar?: Record<string, any> | null;
  avatarType?: number;
  time?: number;
  /** Dados do Humanograma (vêm prontos do $lookup em player_fields__c) */
  funcao?: string;
  prefixo?: string | number;
  nome_dependencia?: string;
}

/**
 * Definição de uma posição do álbum (vem de copa.positions na config).
 * `tipo` e `behavior` definem qual figurinha é compatível (RN-20).
 */
export interface PositionDefinition {
  position: string;
  tipo: StickerType;
  behavior: string;
}

/**
 * Slot do álbum montado no client: definição da posição + figurinha colada (se houver).
 */
export interface AlbumSlot extends PositionDefinition {
  sticker?: CollatedSticker;
}

export type CampaignPhase =
  | 'ESQUENTA'
  | 'LARGADA'
  | 'ROTINA'
  | 'RODADA_DUPLA'
  | 'RETA_FINAL'
  | 'APITO_FINAL'
  | 'ENCERRADA';

export interface CopaAlbumState {
  /** Array PLANO de figurinhas coladas (não é uma grade pré-montada) */
  album: CollatedSticker[];
  repository: RepositorySticker[];
  completedCount: number;
  /** Já abriu o Pacote do Dia HOJE (fuso SP) — fonte da verdade do backend */
  openedToday: boolean;
  /** Já recebeu/abriu o pacote de boas-vindas (day="onboarding") — legado, não usado */
  hasOnboardingPack: boolean;
  /** Já concluiu o onboarding (tutorial) — marcador de backend (sobrevive a logout) */
  hasOnboarded: boolean;
  /** Dias distintos de Pacote do Dia abertos (Estádio BB, meta 8) */
  estadioDays: number;
  /** Comportamentos distintos enviados pelo jogador (Mascote, meta 5) */
  mascoteBehaviors: number;
}

/** Tipo de envelope (cada doc copa_pacote__c = 1 envelope com 1 figurinha) */
export type PackKind = 'daily' | 'bonus' | 'rodada_dupla' | 'onboarding';

/** Resposta do before_create trigger ao abrir o pacote */
export interface OpenPackResponse {
  stickers: Sticker[];
  errors?: BackendError[];
  /**
   * Mensagem informativa retornada quando o pool está vazio (EC-12).
   * Neste caso stickers[] estará vazio e NÃO deve haver animação de abertura.
   */
  message?: string;
}

/**
 * O que o jogador pode abrir agora (prepared copa_packs_pending, RN-15/16/ADR-16).
 * Cada envelope é aberto via POST separado (1 figurinha cada).
 */
export interface PacksPending {
  /** Pode abrir o Pacote do Dia hoje */
  canDaily: boolean;
  /** Rodada Dupla ativa e 2º pacote do dia ainda não aberto */
  canRodadaDupla: boolean;
  /** sourceRecognitionId de cada envelope bônus pendente (26–30/jun) */
  bonusPending: string[];
}

/** Erro de validação retornado pelos triggers (HTTP 200, no body) */
export interface BackendError {
  field?: string;
  message: string;
}

/** Janela de uma fase da campanha (vem de copa.states na config) */
export interface CampaignState {
  state: CampaignPhase;
  start: string; // 'YYYY-MM-DD'
  end: string; // 'YYYY-MM-DD'
}

/** Bloco copa dentro do documento global__c */
export interface CopaConfig {
  enabled: boolean;
  doublePack?: boolean;
  states: CampaignState[];
  positions: PositionDefinition[];
}

/** Payload de colagem — o trigger precisa da figurinha completa */
export interface CollatePayload {
  position: string;
  tipo: StickerType;
  behavior: string;
  recognitionId: string;
  colleagueId: string;
}

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

/** Lança um Error com a mensagem do primeiro `errors[]` do body, se houver. */
function throwIfBackendErrors(data: { errors?: BackendError[] } | undefined) {
  if (data?.errors?.length) {
    throw new Error(data.errors[0].message);
  }
}

// ---------------------------------------------------------------------------
// Chamadas de API
// ---------------------------------------------------------------------------

/**
 * Abre UM envelope (1 figurinha). O before_create trigger `copa_abrir_pacote`
 * faz o sorteio server-side e retorna o entity inserido com `stickers[]` (1 item).
 *
 * O `kind` distingue o tipo de envelope (RN-14/15/16/ADR-16):
 *   - daily        Pacote do Dia (1×/dia, cooldown)
 *   - rodada_dupla 2º pacote do dia (7–13/jul)
 *   - bonus        envelope por reconhecimento enviado (26–30/jun); requer sourceRecognitionId
 *   - onboarding   pacote de boas-vindas (idempotente)
 *
 * Rejeições (campanha desabilitada, cooldown) voltam como HTTP 200 com `body.errors[]`.
 *
 * @param options.kind  tipo de envelope (default 'daily')
 * @param options.sourceRecognitionId  id do reconhecimento que gerou o bônus (obrigatório p/ kind='bonus')
 * @param options.onboarding  atalho legado p/ kind='onboarding'
 */
export async function openPack(options?: {
  kind?: PackKind;
  sourceRecognitionId?: string;
  onboarding?: boolean;
}): Promise<OpenPackResponse> {
  const kind: PackKind = options?.onboarding ? 'onboarding' : options?.kind ?? 'daily';
  const body: Record<string, unknown> = { kind };
  if (kind === 'onboarding') body.day = 'onboarding';
  if (kind === 'bonus' && options?.sourceRecognitionId) {
    body.sourceRecognitionId = options.sourceRecognitionId;
  }
  const response = await api.post('database/copa_pacote__c', body);
  const data = response.data as OpenPackResponse;
  throwIfBackendErrors(data);
  return data;
}

/**
 * Lê os envelopes que o jogador pode abrir agora (prepared `copa_packs_pending`).
 * O player é injetado server-side pelo token — não enviar no body.
 */
export async function getPacksPending(): Promise<PacksPending> {
  const r = await api.post('find/copa_packs_pending', {});
  const raw = r.data;
  const doc = (Array.isArray(raw) ? raw[0] : raw) as Partial<PacksPending> | undefined;
  return {
    canDaily: doc?.canDaily ?? false,
    canRodadaDupla: doc?.canRodadaDupla ?? false,
    bonusPending: Array.isArray(doc?.bonusPending) ? doc!.bonusPending! : [],
  };
}

/**
 * Cola uma figurinha do repositório em uma posição do álbum.
 * O after_create trigger `copa_colar` valida RN-20 (behavior↔posição) e RN-18/28 (índices únicos).
 *
 * O trigger precisa da figurinha COMPLETA (não apenas id + posição).
 * Rejeições voltam como HTTP 200 com `body.errors[]`.
 *
 * @param payload  posição-alvo + dados da figurinha selecionada
 */
export async function collate(payload: CollatePayload): Promise<{ errors?: BackendError[] }> {
  const response = await api.post('database/copa_album__c', payload);
  const data = (response.data ?? {}) as { errors?: BackendError[] };
  throwIfBackendErrors(data);
  return data;
}

/**
 * Substitui (troca) a figurinha de uma posição já preenchida por outra que o
 * jogador possua para a mesma posição (RF-08 / RN-31 / ADR-20).
 *
 * Faz **PUT** em `database/copa_album__c` com o **documento completo no corpo**
 * (incl. `_id`, **sem** `_id` na URL — full-replace). A trigger `before_update`
 * `copa_substituir` valida (`tipo`+`behavior` ↔ posição, posição inalterada,
 * figurinha pertence à Coleção), **preserva o `time` original**, **NÃO emite
 * `copa_collate`** (a posição já foi contada) e grava a telemetria `substitute`
 * server-side. `player` é injetado pela trigger (token), não enviar no corpo.
 *
 * Rejeições voltam como HTTP 200 com `body.errors[]` (Modo B) ou erro HTTP
 * (índice único `{player+colleagueId}` quando o colega já está em outra posição — EC-14).
 *
 * @param current      figurinha colada atual (precisa de `_id`, `position`, `tipo`, `behavior`)
 * @param replacement  nova figurinha escolhida na Coleção (mesmo `tipo`+`behavior`)
 */
export async function substitute(
  current: CollatedSticker,
  replacement: RepositorySticker,
): Promise<{ errors?: BackendError[] }> {
  if (!current._id) {
    throw new Error('Figurinha sem identificador — recarregue o álbum e tente novamente.');
  }
  // Doc completo (full-replace): `position`/`tipo`/`behavior` são os da POSIÇÃO
  // (autoritativos); `colleagueId`/`recognitionId` vêm da nova figurinha. O `time`
  // ecoa o original (round-trip preserva valor/tipo; a trigger também o reforça).
  const fullDoc: Record<string, unknown> = {
    _id: current._id,
    position: current.position,
    tipo: current.tipo,
    behavior: current.behavior,
    recognitionId: replacement.recognitionId,
    colleagueId: replacement.colleagueId,
  };
  if (current.time != null) fullDoc.time = current.time;
  const response = await api.put('database/copa_album__c', fullDoc);
  const data = (response.data ?? {}) as { errors?: BackendError[] };
  throwIfBackendErrors(data);
  return data;
}

/**
 * Lê o estado completo do álbum do jogador logado.
 * Usa o prepared aggregate `copa_album_state` (POST /v3/find/{id}).
 * O player é injetado server-side pelo token — não enviar no body.
 *
 * `album` é um array PLANO de figurinhas coladas; a grade é montada no client
 * a partir de copa.positions (ver buildAlbumSlots).
 */
export async function getAlbum(): Promise<CopaAlbumState> {
  const response = await api.post('find/copa_album_state', {});
  // POST /v3/find/{id} retorna um ARRAY de documentos — o estado é o 1º item.
  const raw = response.data;
  const data = (Array.isArray(raw) ? raw[0] : raw) as Partial<CopaAlbumState> | undefined;
  return {
    album: data?.album ?? [],
    repository: data?.repository ?? [],
    completedCount: data?.completedCount ?? 0,
    openedToday: data?.openedToday ?? false,
    hasOnboardingPack: data?.hasOnboardingPack ?? false,
    hasOnboarded: data?.hasOnboarded ?? false,
    estadioDays: data?.estadioDays ?? 0,
    mascoteBehaviors: data?.mascoteBehaviors ?? 0,
  };
}

/**
 * Retorna os 3 desafios da Copa 2026 com progresso do jogador.
 * Reutiliza getChallengeUser e filtra client-side por extra.type === 'copa'.
 */
export async function getCopaChallenges(cycleId?: string): Promise<any[]> {
  const all = await getChallengeUser(cycleId);
  return all.filter((item: any) => item?.extra?.type === 'copa');
}

/**
 * Lê o bloco `copa` da configuração global (documento 'global' de global__c).
 * Mesmo padrão usado por surpriseEnabled/rouletteEnabled (getGlobalConfig retorna array).
 * Retorna null se a config não estiver disponível.
 */
export async function getCopaConfig(): Promise<CopaConfig | null> {
  const data = await getGlobalConfig('global');
  const doc = Array.isArray(data) ? data[0] : data;
  const copa = doc?.copa;
  if (!copa) return null;
  return {
    enabled: copa.enabled !== false,
    doublePack: copa.doublePack === true,
    states: Array.isArray(copa.states) ? copa.states : [],
    positions: Array.isArray(copa.positions) ? copa.positions : [],
  };
}

// ---------------------------------------------------------------------------
// Derivações client-side
// ---------------------------------------------------------------------------

/** Data de hoje em America/Sao_Paulo no formato 'YYYY-MM-DD'. */
function todayInSaoPaulo(): string {
  return format(utcToZonedTime(new Date(), TIMEZONE), 'yyyy-MM-dd');
}

/**
 * Calcula a fase atual da campanha a partir da config.
 * - Se copa.enabled === false → ENCERRADA.
 * - Se hoje > fim do APITO_FINAL → ENCERRADA.
 * - Caso contrário, casa a data de hoje (America/Sao_Paulo) contra states[].
 *
 * `phaseEndAt` é o fim da fase atual em ISO (00:00 do dia seguinte ao `end`, fuso SP),
 * usado para a contagem regressiva do CampaignBanner.
 */
export function getCampaignPhase(
  config: CopaConfig | null,
): { phase?: CampaignPhase; phaseEndAt?: string } {
  if (!config || !config.states.length) return {};

  const today = todayInSaoPaulo();
  const apito = config.states.find(s => s.state === 'APITO_FINAL');

  if (!config.enabled || (apito && today > apito.end)) {
    return { phase: 'ENCERRADA' };
  }

  const current = config.states.find(s => today >= s.start && today <= s.end);
  if (!current) {
    // Antes do início → ESQUENTA; depois do fim → ENCERRADA
    const first = config.states.reduce((a, b) => (a.start < b.start ? a : b));
    if (today < first.start) return { phase: 'ESQUENTA' };
    return { phase: 'ENCERRADA' };
  }

  // Fim da fase = 00:00 do dia seguinte ao `end`, no fuso SP
  const phaseEndAt = `${current.end}T23:59:59-03:00`;
  return { phase: current.state, phaseEndAt };
}

/** Detalhe do reconhecimento de origem de uma figurinha (RN-30, prepared copa_recognition) */
export interface RecognitionDetail {
  recognitionId: string;
  behavior: string;
  /** Mensagem do reconhecimento (HTML) */
  message?: string;
  /** Moeda/selo do reconhecimento (uau/mandou/valeu) */
  coin?: string;
  /** 'recebido' = colega te reconheceu (Titular); 'enviado' = você reconheceu (Craque/Técnico) */
  direction: 'recebido' | 'enviado';
  colleagueId: string;
  colleagueName?: string;
  image?: { small?: { url?: string } } | null;
  avatar?: Record<string, any> | null;
  avatarType?: number;
  time?: number;
  /** Dados do Humanograma (vêm prontos do $lookup em player_fields__c) */
  funcao?: string;
  prefixo?: string | number;
  nome_dependencia?: string;
}

/**
 * Lê o reconhecimento de origem de uma figurinha (RN-30).
 * Anti-cheat: o backend só retorna se o `recognitionId` pertence ao pool do jogador
 * (userId==player ou target==player). Retorna null se não encontrado/sem acesso.
 */
export async function getRecognition(recognitionId: string): Promise<RecognitionDetail | null> {
  const r = await api.post('find/copa_recognition', { recognitionId });
  const raw = r.data;
  const doc = (Array.isArray(raw) ? raw[0] : raw) as RecognitionDetail | undefined;
  return doc ?? null;
}

/**
 * Retorna o número de figurinhas disponíveis no pool do jogador para sorteio.
 * Usado pela tela de abertura de pacote para decidir se exibe a CTA (RN-29 / EC-12).
 * O player é injetado server-side pelo token — não enviar no body.
 */
export async function getStickerAvailable(): Promise<number> {
  const r = await api.post('find/copa_sticker_available', {});
  // POST /v3/find/{id} retorna um ARRAY — o doc {available:N} é o 1º item.
  const raw = r.data;
  const doc = Array.isArray(raw) ? raw[0] : raw;
  return (doc?.available ?? 0) as number;
}

// ---------------------------------------------------------------------------
// Lendárias (challenge-based, não vivem em copa_album__c)
// ---------------------------------------------------------------------------

/** Posições das 3 Lendárias e o índice da rule no desafio FyC6agj */
export const LEGENDARY_POSITIONS = ['lendaria_mascote', 'lendaria_estadio', 'lendaria_taca'] as const;
const LEGENDARY_RULE_INDEX: Record<string, number> = {
  lendaria_mascote: 0,
  lendaria_estadio: 1,
  lendaria_taca: 2,
};

/** Encontra o desafio "Lendárias" (FyC6agj) na lista filtrada de desafios da Copa. */
export function findLegendaryChallenge(challenges: any[]): any {
  return challenges.find(
    (c: any) => c?._id === 'FyC6agj' || c?.extra?.order === 3 || c?.extra?.total === 3,
  );
}

/**
 * true = o desafio inteiro foi concluído.
 *
 * Nesta instância um desafio NUNCA persiste a 100% em `challenge_progress`: ao
 * atingir a meta, o progresso é removido e a conclusão vira um `achievement`
 * type 1 (item = id do desafio). O prepared `find/challenge` expõe isso no campo
 * numérico `completed` (soma de achievement.total na janela do ciclo). É o mesmo
 * sinal que a página nativa de Desafios usa (`item.completed`) — o `progress`
 * serve só para o estado PARCIAL enquanto o desafio está em andamento.
 */
export function isChallengeCompleted(challenge: any): boolean {
  const c = challenge?.completed;
  return typeof c === 'number' ? c > 0 : !!c;
}

/** true = a lendária da `position` está conquistada (rule concluída no desafio). */
export function isLegendaryDone(challenges: any[], position: string): boolean {
  const ch = findLegendaryChallenge(challenges);
  if (!ch) return false;
  // Bloco inteiro concluído: o `progress` some e a conclusão vira achievement.
  // Sem `progress` + `completed` ⇒ as 3 lendárias do desafio foram conquistadas.
  if (!ch.progress && isChallengeCompleted(ch)) return true;
  // Parcial: enquanto o `progress` existe, cada rule carrega seu próprio estado.
  const rule = ch.progress?.rules?.[LEGENDARY_RULE_INDEX[position]];
  return (
    !!rule?.completed ||
    (typeof rule?.times_completed === 'number' &&
      rule.times_completed >= (rule.times_required ?? 1))
  );
}

/** Quantas das 3 Lendárias já foram conquistadas (0–3). */
export function countLegendariesDone(challenges: any[]): number {
  return LEGENDARY_POSITIONS.filter(p => isLegendaryDone(challenges, p)).length;
}

/**
 * Monta a grade de slots do álbum a partir das posições da config e das
 * figurinhas coladas (array plano). Um slot fica preenchido quando existe
 * um documento colado com a mesma `position`.
 */
export function buildAlbumSlots(
  positions: PositionDefinition[],
  collated: CollatedSticker[],
): AlbumSlot[] {
  const byPosition = new Map<string, CollatedSticker>();
  collated.forEach(doc => {
    if (doc?.position) byPosition.set(doc.position, doc);
  });
  return positions.map(def => ({
    ...def,
    sticker: byPosition.get(def.position),
  }));
}
